import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import ProductThumb from "@/components/ProductThumb.jsx";
import ProductCatalogRatingBadge from "@/components/ProductCatalogRatingBadge.jsx";
import Price from "@/components/Price.jsx";
import { lowestProductPrice } from "@/utils/productPricing.js";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/bodyScrollLock.js";
import { useEffect } from "react";
import "./product-highlight-popup.css";

const KIND_META = {
    NEW: {
        badge: "Nouveau",
        className: "product-highlight-modal--new",
        icon: (
            <svg viewBox="0 0 24 24" aria-hidden="true" className="product-highlight-badge-icon">
                <path
                    fill="currentColor"
                    d="M12 2.5l2.2 5.4 5.8.5-4.4 3.8 1.3 5.6L12 14.9 6.1 17.8l1.3-5.6L3 8.4l5.8-.5L12 2.5z"
                />
            </svg>
        )
    },
    RESTOCKED: {
        badge: "De retour",
        className: "product-highlight-modal--restocked",
        icon: (
            <svg viewBox="0 0 24 24" aria-hidden="true" className="product-highlight-badge-icon">
                <path
                    fill="currentColor"
                    d="M12 5V2L8 6l4 4V7c2.8 0 5 2.2 5 5a5 5 0 0 1-5 5 5 5 0 0 1-4.9-4H7.1A7 7 0 0 0 12 19a7 7 0 0 0 7-7c0-3.9-3.1-7-7-7z"
                />
            </svg>
        )
    }
};

export default function ProductHighlightPopup({ kind, product, onClose }) {
    const meta = KIND_META[kind] || KIND_META.NEW;
    const navigate = useNavigate();
    const productPath = `/product/${product.id}`;

    useEffect(() => {
        lockBodyScroll();
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => {
            unlockBodyScroll();
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [onClose]);

    const goToProduct = () => {
        onClose();
        navigate(productPath);
    };

    return createPortal(
        <div
            className="product-highlight-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="product-highlight-title"
            onClick={onClose}
        >
            <div
                className={`product-highlight-modal card ${meta.className}`}
                onClick={(event) => event.stopPropagation()}
            >
                <div className="product-highlight-glow" aria-hidden="true" />

                <div className="product-highlight-top">
                    <span className="product-highlight-badge">
                        {meta.icon}
                        {meta.badge}
                    </span>
                    <button type="button" className="link-btn product-highlight-close" onClick={onClose} aria-label="Fermer">
                        Fermer
                    </button>
                </div>

                <button
                    type="button"
                    className="product-highlight-media"
                    onClick={goToProduct}
                    aria-label={`Voir l'article : ${product.title}`}
                >
                    <ProductCatalogRatingBadge averageGrade={product.averageGrade} />
                    <ProductThumb
                        fileId={product.photoIds?.[0] || product.photoId}
                        alt={product.title}
                        placeholder={product.category?.[0] || "P"}
                    />
                </button>

                <button
                    type="button"
                    className="product-highlight-title-btn"
                    onClick={goToProduct}
                >
                    <h2 id="product-highlight-title" className="product-highlight-title">
                        {product.title}
                    </h2>
                </button>

                <p className="product-highlight-price">
                    <Price value={lowestProductPrice(product)} />
                </p>

                <div className="product-highlight-actions">
                    <Link
                        to={productPath}
                        className="btn btn-primary"
                        onClick={onClose}
                    >
                        Voir l&apos;article
                    </Link>
                </div>
            </div>
        </div>,
        document.body
    );
}
